import { useEffect, useState } from 'react';
import { Button, Col, Divider, Empty, Layout, Popconfirm, Result, Row, Space, Table, Tooltip, Typography, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { BarChartOutlined, CheckCircleFilled, CheckOutlined, ClockCircleOutlined, LockOutlined, PlusOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { EventType } from '../../Types/EventType';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ColumnsType } from 'antd/lib/table';
import { EventService } from '../../Services/EventService';
import { formatDateRangeWithDateString, formatDateString } from '../../Helpers/DateHelpers';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import Page from '../../Components/Page/Page';
import { EventStatusTag, getEventListStatus } from '../../Components/EventDetails/EventStatusTag';

const MyEvents = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([] as any);
  const [selectedEvent, setSelectedEvent] = useState([] as any);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isCreated, setIsCreated] = useState(undefined as string | undefined);
  // Until the events have loaded, the table shows a spinner instead of the empty state.
  const [loading, setLoading] = useState(true);

  const GetText = TextService.GetText;
  let params = useParams();


  useEffect(() => {
    if (params != null) {
      setIsCreated(params.newid);
    }
    loadEvents();
  }, []);

  const loadEvents = () => {
    EventService.GetMyEvents().then(result => {
      const events: ColumnsType<DataType> = result;
      setEvents(events);
    }).finally(() => setLoading(false));
  }

  const navigateToEvent = (uniqueId: string) => {
    navigation("/" + getCurrentLanguage() + "/myevents/" + uniqueId);
  }

  // The whole row opens the event. Buttons, links and the delete confirmation (rendered outside the row,
  // but its clicks still reach the row through React) keep their own action. Ctrl/Cmd opens a new tab.
  const onRowClick = (e: React.MouseEvent, uniqueId: string) => {
    if ((e.target as HTMLElement).closest("button, a, .ant-popover")) {
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      window.open("/" + getCurrentLanguage() + "/myevents/" + uniqueId, "_blank");
      return;
    }

    navigateToEvent(uniqueId);
  }

  const oncreateclick = () => {
    navigation("/" + getCurrentLanguage() + "/create/");
  }

  // A new association sees what to do next instead of an empty table.
  const locale = {
    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={GetText("MyEvents_NoEvents")}>
      <Button type="primary" icon={<PlusOutlined />} onClick={oncreateclick}>{GetText("MyEvents_CreateFirstEvent")}</Button>
    </Empty>
  }

  const onDeleteClick = (id: number) => {
    events.forEach((e: { id: number; showDelete: boolean; }) => {
      if (e.id === id) {
        if (e.showDelete === false || e.showDelete === undefined) {
          e.showDelete = true;
          setEvents(events);
          return;
        }
      }
    });

    setSelectedEvent(id);
  };

  const handleOk = async () => {
    await EventService.DeleteEvent(selectedEvent);
    setConfirmLoading(false);
    loadEvents();
  };

  const handleCancel = () => {
    events.forEach((e: { id: number; showDelete: boolean; }) => {
      e.showDelete = false;
    });

    setEvents((events: any) => ([
      ...events,

    ]));
  };

  interface DataType {
    key: string;
    name: string;
    id: number;
    uniqueId: string;
    numberOfTimes: number;
    numberOfReplies: number;
    createdAt: string;
    times: any[];
    tags: string[];
    showDelete: boolean;
    state: number;
    deadline?: string;
    membersOnly?: boolean;
    showOnMemberSite?: boolean;
    eventType?: EventType;
  }

  // The same status as on Status & Svar (EventStatusTag).
  const getStatus = (event: DataType) => getEventListStatus(event);

  const renderStatus = (event: DataType) => <EventStatusTag status={getStatus(event)} />;

  // The time of the event that comes first. An event can have several suggested times.
  const getFirstTime = (event: DataType) => {
    const times = [...(event.times ?? [])].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return times.length > 0 ? times[0] : undefined;
  };

  // Events without a time are sorted last.
  const getFirstTimeValue = (event: DataType) => {
    const firstTime = getFirstTime(event);
    return firstTime ? new Date(firstTime.start).getTime() : Number.MAX_SAFE_INTEGER;
  };

  const renderEventDate = (event: DataType) => {
    const firstTime = getFirstTime(event);

    if (!firstTime) {
      return "";
    }

    const moreTimes = event.times.length - 1;

    return <span style={{ whiteSpace: "nowrap" }}>
      {formatDateRangeWithDateString(firstTime.start, firstTime.end, firstTime.isFullDay, getCurrentLanguage() === "se")}
      {moreTimes > 0 ? <span style={{ color: "#888" }}> +{moreTimes}</span> : ""}
    </span>;
  };

  const columns: ColumnsType<DataType> = [
    {
      title: GetText("MyEvents_EventName"),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? "", getCurrentLanguage() === "se" ? "sv" : "en"),
      // A sign-up open to everyone is the default and gets no icon - only the exceptions are marked.
      render: (_, event) => (
        <Space size={6}>
          <a onClick={() => navigateToEvent(event.uniqueId)}><b>{event.name}</b></a>
          {event.eventType === EventType.Poll ? <Tooltip title={GetText("MyEvents_PollTooltip")}><BarChartOutlined style={{ color: "#888" }} aria-label={GetText("MyEvents_PollTooltip")} /></Tooltip> : ""}
          {event.membersOnly ? <Tooltip title={GetText("MyEvents_MembersOnlyTooltip")}><LockOutlined style={{ color: "#888" }} aria-label={GetText("MyEvents_MembersOnlyTooltip")} /></Tooltip> : ""}
          {event.showOnMemberSite ? <Tooltip title={GetText("MyEvents_ShowOnMemberSiteTooltip")}><TeamOutlined style={{ color: "#888" }} aria-label={GetText("MyEvents_ShowOnMemberSiteTooltip")} /></Tooltip> : ""}
        </Space>
      ),
    },
    {
      title: GetText("MyEvents_Status"),
      key: 'status',
      sorter: (a, b) => getStatus(a) - getStatus(b),
      render: (_, event) => renderStatus(event),
    },
    {
      title: GetText("MyEvents_EventDate"),
      key: 'eventDate',
      sorter: (a, b) => getFirstTimeValue(a) - getFirstTimeValue(b),
      render: (_, event) => renderEventDate(event),
    },
    {
      title: GetText("MyEvents_Deadline"),
      dataIndex: 'deadline',
      key: 'deadline',
      // Events without a deadline are sorted last.
      sorter: (a, b) => (a.deadline ? new Date(a.deadline).getTime() : Number.MAX_SAFE_INTEGER) - (b.deadline ? new Date(b.deadline).getTime() : Number.MAX_SAFE_INTEGER),
      render: text => text ? <span style={{ whiteSpace: "nowrap" }}>{formatDateString(text)}</span> : <span style={{ color: "#888" }}>–</span>,
    },
    {
      title: GetText("MyEvents_SuggestedTimes"),
      dataIndex: 'numberOfTimes',
      key: 'numberOfTimes',
      sorter: (a, b) => a.numberOfTimes - b.numberOfTimes,
      render: text => <span><ClockCircleOutlined /> {text}</span>,
    },
    {
      title: GetText("MyEvents_Replies"),
      dataIndex: 'numberOfReplies',
      key: 'numberOfReplies',
      sorter: (a, b) => a.numberOfReplies - b.numberOfReplies,
      render: text => text === 0 ? <span className='no-replies'><UserOutlined /> {text}</span> : <span><UserOutlined /> {text}</span>,
    },
    {
      title: GetText("MyEvents_Action"),
      key: 'action',
      render: (_, _event) => (
        <Space size="middle">
          <Button onClick={() => onDeleteClick(_event.id)} danger>{GetText("MyEvents_Delete")}</Button>

          <Popconfirm
            title={GetText("MyEvents_DeleteEventConfirm")}
            key={_event.id}
            open={_event.showDelete}
            placement="topRight"
            onConfirm={handleOk}
            okButtonProps={{ loading: confirmLoading }}
            onCancel={handleCancel}
          >
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return <Page>
    <Layout>

      <Row justify="center" align="top">
        <Col xs={24} lg={16}>
          {isCreated ? <div style={{ marginBottom: "10px" }}><CheckCircleFilled style={{ color: "green" }} /> <span>{GetText("MyEvents_CreatedText")}</span> <a href={`/${getCurrentLanguage()}/myevents/${isCreated}`}>{GetText("MyEvents_LinkToEvent")}</a></div> : ""}
        </Col>
      </Row>

      <Row justify="center" align="top">
        <Col xs={24} lg={20} className='bookyBox'>
          <Title style={{ textAlign: "left", marginBottom: 12 }}>{GetText("MyEvents_MyEvents")}</Title>
          <Space size="middle" style={{ width: '100%', justifyContent: 'left', marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={oncreateclick}>{GetText("TopHeader_CreateEvent")}</Button>
            {!loading && events.length > 0 ?
              <Typography.Text type="secondary">{events.length} {GetText(events.length === 1 ? "MyEvents_CountOne" : "MyEvents_CountMany")}</Typography.Text>
              : null}
          </Space>
          <Table columns={columns} locale={locale} loading={loading} rowKey="id" dataSource={events} showSorterTooltip={false} scroll={{ x: 'max-content' }}
            onRow={(event) => ({ onClick: (e) => onRowClick(e, event.uniqueId), style: { cursor: "pointer" } })} />
        </Col>
      </Row>
      <Divider />
    </Layout>
  </Page>
};

export default MyEvents;